import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    TextField,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Switch,
    FormControlLabel,
    Autocomplete,
    ToggleButton,
    ToggleButtonGroup,
    CircularProgress,
    Alert,
    Paper,
    Grid
} from '@mui/material';
import { API_BASE_URL } from './config';
import { useTranslation } from 'react-i18next';

// Simple debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

const FlightSearchPage = () => {
    const { t, i18n } = useTranslation();
    // Form State
    const [origin, setOrigin] = useState('');
    const [destination, setDestination] = useState('');
    const [originIata, setOriginIata] = useState('');
    const [destinationIata, setDestinationIata] = useState('');
    const [tripType, setTripType] = useState('round-trip'); // 'round-trip' or 'one-way'
    const [dateMode, setDateMode] = useState('exact'); // 'exact' or 'duration'

    // Exact Date State
    const [departureDate, setDepartureDate] = useState('');
    const [returnDate, setReturnDate] = useState('');

    // Duration Date State
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [flexibleDurationType, setFlexibleDurationType] = useState('Week');

    const [passengers, setPassengers] = useState(1);

    // Autocomplete State
    const [originSuggestions, setOriginSuggestions] = useState([]);
    const [destinationSuggestions, setDestinationSuggestions] = useState([]);
    const debouncedOrigin = useDebounce(origin, 300);
    const debouncedDestination = useDebounce(destination, 300);

    // Search State
    const [searching, setSearching] = useState(false);
    const [searchResults, setSearchResults] = useState(null);
    const [error, setError] = useState('');

    // Autocomplete Logic
    const fetchSuggestions = useCallback(async (query, setSuggestions) => {
        if (query.length < 3) {
            setSuggestions([]);
            return;
        }
        try {
            const response = await fetch(`${API_BASE_URL}/autocomplete.php?q=${encodeURIComponent(query)}`);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const data = await response.json();
            const mappedSuggestions = data.map(s => ({ ...s, icon: s.description.includes('(') ? '✈️' : '🏙️' }));
            setSuggestions(mappedSuggestions);
        } catch (error) {
            console.error("Error fetching autocomplete suggestions:", error);
            setSuggestions([]);
        }
    }, []);

    useEffect(() => {
        fetchSuggestions(debouncedOrigin, setOriginSuggestions);
    }, [debouncedOrigin, fetchSuggestions]);

    useEffect(() => {
        fetchSuggestions(debouncedDestination, setDestinationSuggestions);
    }, [debouncedDestination, fetchSuggestions]);

    const handleSelectSuggestion = (suggestion, setter, iataSetter, suggestionSetter) => {
        setter(suggestion.description);
        const iataMatch = suggestion.description.match(/\((.*?)\)/);
        if (iataMatch && iataMatch[1]) {
            iataSetter(iataMatch[1]);
        }
        suggestionSetter([]);
    };

    // Search Logic
    const handleSearch = async () => {
        setSearching(true);
        setError('');
        setSearchResults(null);

        // Validate that origin and destination are filled
        if (!origin || !destination) {
            setError(t('origin_destination_required') || 'Please enter both origin and destination');
            setSearching(false);
            return;
        }

        const token = localStorage.getItem('token');
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };

        let endpoint = '';
        let body = {
            origin: originIata || origin,
            destination: destinationIata || destination,
            oneWay: tripType === 'one-way',
            adults: passengers || 1,
            language: i18n.language
        };

        if (dateMode === 'exact') {
            endpoint = `${API_BASE_URL}/flight_search.php`;
            body.date = departureDate;
            if (tripType === 'round-trip') {
                body.returnDate = returnDate;
            }
        } else { // duration mode
            endpoint = `${API_BASE_URL}/flexible_flight_search.php`;
            body.month = month;
            body.year = year;
            body.durationType = flexibleDurationType;
        }

        try {
            const response = await fetch(endpoint, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify(body)
            });

            if (response.ok) {
                const data = await response.json();
                setSearchResults(data);
            } else {
                const errorData = await response.json();
                setError(errorData.message || t('fetch_flight_results_error'));
            }
        } catch (err) {
            console.error("Flight search error:", err);
            setError(t('flight_search_error'));
        } finally {
            setSearching(false);
        }
    };
    
    const renderFlightInfo = (flight) => {
        if (!flight) {
            return <p>{t('no_flight_info')}</p>;
        }

        const formatISODuration = (duration) => {
            if (!duration) return '';
            const match = duration.match(/PT(\d+H)?(\d+M)?/);
            if (!match) return duration;
        
            const hours = match[1] ? parseInt(match[1].slice(0, -1)) : 0;
            const minutes = match[2] ? parseInt(match[2].slice(0, -1)) : 0;
        
            return `${hours}h ${minutes}m`;
        };
    
        const formatDateTime = (dateTimeString) => {
            const options = {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                hour12: true,
            };
            return new Date(dateTimeString).toLocaleString(undefined, options);
        };

        const renderItinerary = (segments, layovers, carrierInfo, origin, destination) => {
            const stops = segments.length - 1;
            const airline = carrierInfo[0] ? `${carrierInfo[0].carrierName} (${carrierInfo[0].carrierCode})` : '';
            return (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1">
                        {origin} &rarr; {destination} ({stops} {stops !== 1 ? t('stops') : t('stop')}) - {airline}
                    </Typography>
                    {segments.map((segment, index) => (
                        <div key={index}>
                            <Typography variant="body2">
                                {segment.departureAirport} &rarr; {segment.arrivalAirport}: {formatDateTime(segment.departureTime)} -&gt; {formatDateTime(segment.arrivalTime)} ({formatISODuration(segment.duration)})
                            </Typography>
                            {index < segments.length - 1 && layovers && layovers[index] &&
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                    {t('layover_at')} {layovers[index].airportCode}: {formatISODuration(layovers[index].duration)}
                                </Typography>
                            }
                        </div>
                    ))}
                </Box>
            );
        };

        try {
            const segments = flight.flightTimes || [];
            const midPoint = Math.floor(segments.length / 2);
            const outboundSegments = segments.slice(0, midPoint);
            const inboundSegments = segments.slice(midPoint);
            
            const layovers = flight.layoverTimes || [];
            const outboundLayovers = layovers.slice(0, outboundSegments.length - 1);
            const inboundLayovers = layovers.slice(outboundSegments.length - 1);

            const carrierInfo = flight.carrierInformation || [];
            const outboundCarrierInfo = carrierInfo.slice(0, outboundSegments.length);
            const inboundCarrierInfo = carrierInfo.slice(outboundSegments.length);

            return (
                <Paper elevation={1} sx={{ p: 2, mt: 1 }}>
                    {flight.source && <Typography variant="caption">{t('source_label')} {flight.source}</Typography>}
                    {renderItinerary(outboundSegments, outboundLayovers, outboundCarrierInfo, flight.originAirportCode, flight.destinationAirportCode)}
                    {inboundSegments.length > 0 && renderItinerary(inboundSegments, inboundLayovers, inboundCarrierInfo, flight.destinationAirportCode, flight.originAirportCode)}

                    {flight.allowedLuggage && (
                        <Typography>
                            {t('luggage_carry_on')} {flight.allowedLuggage.carryOn ? t('yes') : t('no')}, {t('checked_bags')} {flight.allowedLuggage.checkedBags}
                        </Typography>
                    )}
                    <Typography>{t('total_price')} ${flight.totalPrice} USD</Typography>
                </Paper>
            );
        } catch (e) {
            console.error("renderFlightInfo: Error during JSX rendering:", e, "Flight object:", flight);
            return <Alert severity="error">{t('render_flight_error')}</Alert>;
        }
    };

    const renderFlightResults = (results) => {
        const suggestionMap = {
            [t('suggestion_cheapest')]: results.cheapest,
            [t('suggestion_shortest')]: results.shortest_direct,
            [t('suggestion_smart_stopover')]: results.smart_stopover,
            [t('suggestion_ai_choice')]: results.ai_choice,
        };

        const uniqueFlights = {};

        for (const title in suggestionMap) {
            const flight = suggestionMap[title];
            if (flight) {
                const flightKey = JSON.stringify(flight);
                if (uniqueFlights[flightKey]) {
                    if (flight.categories) {
                        uniqueFlights[flightKey].titles = flight.categories;
                    } else {
                        uniqueFlights[flightKey].titles.push(title);
                    }
                } else {
                    uniqueFlights[flightKey] = {
                        flight,
                        titles: flight.categories || [title],
                    };
                }
            }
        }

        return (
            <Box sx={{ mt: 2 }}>
                {Object.values(uniqueFlights).map(({ flight, titles }, index) => (
                    <Box key={index} sx={{ mt: 2 }}>
                        <Typography variant="h6">{titles.join(' & ')}</Typography>
                        {flight.ai_reason && <Alert severity="info">{flight.ai_reason}</Alert>}
                        {renderFlightInfo(flight)}
                    </Box>
                ))}
            </Box>
        );
    };

    const today = new Date().toISOString().split('T')[0];

    return (
        <Box sx={{ p: 3, maxWidth: 800, margin: 'auto' }}>
            <Typography variant="h4" component="h1" gutterBottom>
                {t('flight_search_title')}
            </Typography>

            <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2} direction="column">
                    <Grid item xs={12}>
                        <ToggleButtonGroup
                            value={tripType}
                            exclusive
                            onChange={(e, newTripType) => {
                                if (newTripType) setTripType(newTripType);
                            }}
                            aria-label="trip type"
                        >
                            <ToggleButton value="round-trip" aria-label="round trip">
                                {t('round_trip')}
                            </ToggleButton>
                            <ToggleButton value="one-way" aria-label="one way">
                                {t('one_way')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    <Grid item xs={12}>
                        <Autocomplete
                            freeSolo
                            options={originSuggestions}
                            getOptionLabel={(option) => option.description || ""}
                            onInputChange={(event, newInputValue) => {
                                setOrigin(newInputValue);
                                // Extract IATA code from input value
                                const iataMatch = newInputValue.match(/\((.*?)\)/);
                                if (iataMatch && iataMatch[1]) {
                                    setOriginIata(iataMatch[1]);
                                } else {
                                    // If no IATA code found in input, clear it so we can use the origin name
                                    setOriginIata('');
                                }
                            }}
                            onChange={(event, newValue) => {
                                if (newValue) handleSelectSuggestion(newValue, setOrigin, setOriginIata, setOriginSuggestions);
                            }}
                            inputValue={origin}
                            renderInput={(params) => <TextField {...params} label={t('origin_label')} variant="outlined" fullWidth />}
                            renderOption={(props, option) => <li {...props}>{option.icon} {option.description}</li>}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Autocomplete
                            freeSolo
                            options={destinationSuggestions}
                            getOptionLabel={(option) => option.description || ""}
                            onInputChange={(event, newInputValue) => {
                                console.log('Destination input changed:', newInputValue, 'Suggestions available:', destinationSuggestions.length);
                                setDestination(newInputValue);
                                // Extract IATA code from input value
                                const iataMatch = newInputValue.match(/\((.*?)\)/);
                                if (iataMatch && iataMatch[1]) {
                                    setDestinationIata(iataMatch[1]);
                                    console.log('Found IATA code:', iataMatch[1]);
                                } else {
                                    // If no IATA code found in input, clear it so we can use the destination name
                                    setDestinationIata('');
                                    if (newInputValue) {
                                        console.log('No IATA code found, will use destination name:', newInputValue);
                                    }
                                }
                            }}
                            onChange={(event, newValue) => {
                                console.log('Destination selected from list:', newValue?.description);
                                if (newValue) handleSelectSuggestion(newValue, setDestination, setDestinationIata, setDestinationSuggestions);
                            }}
                            inputValue={destination}
                            renderInput={(params) => <TextField {...params} label={t('destination_label')} variant="outlined" fullWidth />}
                            renderOption={(props, option) => <li {...props}>{option.icon} {option.description}</li>}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <ToggleButtonGroup
                            value={dateMode}
                            exclusive
                            onChange={(e, newDateMode) => {
                                if (newDateMode) setDateMode(newDateMode);
                            }}
                            aria-label="date mode"
                        >
                            <ToggleButton value="exact" aria-label="exact dates">
                                {t('exact_dates')}
                            </ToggleButton>
                            <ToggleButton value="duration" aria-label="month and duration">
                                {t('month_duration')}
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Grid>

                    {dateMode === 'exact' ? (
                        <>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('departure_date_label')}
                                    type="date"
                                    value={departureDate}
                                    onChange={e => {
                                        setDepartureDate(e.target.value);
                                        if (tripType === 'round-trip') {
                                            setReturnDate(e.target.value);
                                        }
                                    }}
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ min: today }}
                                />
                            </Grid>
                            {tripType === 'round-trip' && (
                                <Grid item xs={12}>
                                    <TextField
                                        label={t('return_date_label')}
                                        type="date"
                                        value={returnDate}
                                        onChange={e => setReturnDate(e.target.value)}
                                        fullWidth
                                        InputLabelProps={{ shrink: true }}
                                        inputProps={{ min: departureDate || today }}
                                    />
                                </Grid>
                            )}
                        </>
                    ) : (
                        <>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('month_label')}</InputLabel>
                                    <Select value={month} label={t('month_label')} onChange={e => setMonth(e.target.value)}>
                                        {[...Array(12).keys()].map(i => (
                                            <MenuItem key={i + 1} value={i + 1}>
                                                {new Date(0, i).toLocaleString('default', { month: 'long' })}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    label={t('year_label')}
                                    type="number"
                                    value={year}
                                    onChange={e => setYear(e.target.value)}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <InputLabel>{t('duration_label')}</InputLabel>
                                    <Select value={flexibleDurationType} label={t('duration_label')} onChange={e => setFlexibleDurationType(e.target.value)}>
                                        <MenuItem value={"3-5 days"}>{t('wizard_duration_3days')}</MenuItem>
                                        <MenuItem value={"Week"}>{t('flexible_duration_week')}</MenuItem>
                                        <MenuItem value={"Weekend"}>{t('flexible_duration_weekend')}</MenuItem>
                                        <MenuItem value={"2-3 Weeks"}>{t('flexible_duration_2_3_weeks')}</MenuItem>
                                        <MenuItem value={"Month"}>{t('flexible_duration_month')}</MenuItem>
                                        <MenuItem value={"Month+"}>{t('flexible_duration_month_plus')}</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12}>
                        <TextField
                            label={t('passengers_label')}
                            type="number"
                            value={passengers}
                            onChange={e => {
                                if (e.target.value === '') {
                                    setPassengers('');
                                } else {
                                    const value = parseInt(e.target.value, 10);
                                    if (!isNaN(value) && value > 0) {
                                        setPassengers(value);
                                    }
                                }
                            }}
                            fullWidth
                            InputProps={{ inputProps: { min: 1 } }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSearch}
                            disabled={searching}
                        >
                            {searching ? t('searching') : t('search_flights_btn')}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {searching && <CircularProgress />}
            {error && <Alert severity="error">{error}</Alert>}
            {searchResults && (
                <Box>
                    <Typography variant="h5" component="h2" gutterBottom>
                        {t('search_results_title')}
                    </Typography>
                    {renderFlightResults(searchResults)}
                </Box>
            )}
        </Box>
    );
};

export default FlightSearchPage;
